import React, { useState, useContext, useMemo } from 'react';
import propTypes from 'prop-types';
import styled from 'styled-components';
import Modal from '@material-ui/core/Modal';
import {
  APP,
  RONGCLOUD_CHATROOM_PREFIX,
  BRAND_COLOR,
} from 'appenv';

import ChatContext from 'components/chat/ChatContext';

import InsertInvitationIcon from 'assets/calendarWhite.svg';
import EmailIcon from 'assets/emailWhite.svg';
import chatroomIcon from 'assets/chatroomWhite.svg';
import CloseBtn from 'assets/closeBtn.svg';
import LogoPlaceholder from 'assets/logoPlaceholder.svg';

import playButton from 'assets/play-button.png';
import BoothBreadcrumbs from './components/BoothBreadcrumbs';
import { VideoSources } from './templates/Booth624Template';

const FlexContainer = styled.div`
  width: 100%;
  padding-bottom: 20px;
`;

const LogoContainer = styled.div`
  width: 200px;
  margin: 16px auto;
  text-align: center;
`;

const LogoContainer624 = styled.div`
  width: 200px;
  margin: 16px auto;
  text-align: center;
  cursor: pointer;
  user-select: none;
  position: relative;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
`;

const StyledImg = styled.img`
  width: ${({ hasLogo }) => (hasLogo ? '100%' : '32px')};
  height: ${({ hasLogo }) => (hasLogo ? '100%' : '32px')};
`;

const StyledIcon = styled.img`
  filter: grayscale(100%) brightness(900%);
`;

const NameText = styled.div`
  font-size: 18px;
  line-height: 18px;
  margin: 0 16px 8px;
  text-align: center;
`;

const BackContainer = styled.div`
  padding: 20px;
  width: 100%;
`;

const PopupBtn = styled.div`
  position: fixed;
  bottom: 50px;
  right: 20px;
  width: 60px;
  height: 60px;
  color: #FFF;
  text-align: center;
  line-height: 76px;
  background: ${BRAND_COLOR || '#0CA58B'};
  border-radius: 60px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.24);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BubbleBtnGroup = styled.div`
  position: absolute;
  bottom: 115px;
  right: 26px;
  background-color: rgba(0, 0, 0, 0);
`;

const StyledHyperLink = styled.a`
  text-decoration: none;
  color: #282B40;

  &:visited {
    color: #282B40;
  }
`;

const BunnbleBtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const BubbleBtnText = styled.div`
  font-size: 14px;
  line-height: 14px;
  color: #282B40;
  margin-right: 16px;
`;

const BubbleBtn = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 48px;
  line-height: 65px;
  color: #FFF;
  background: ${BRAND_COLOR || '#0CA58B'};
  text-align: center;
  margin: 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledModal = styled(Modal)`
  background-color: rgba(255, 255, 255, 0.89);

  & > div {
    background-color: rgba(0, 0, 0, 0) !important;

    &:focus {
      outline: none;
    }
  }
`;

const StyledPlayButton = styled.img`
  width: 30px;
  height: auto;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const MobileProfile = ({ booth }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const { showChatWindow } = useContext(ChatContext);

  const mailToLink = useMemo(() => {
    const { emailContacts } = booth;
    if (emailContacts && emailContacts.length > 0) {
      const email = emailContacts.find((eachemailContact) => (eachemailContact.type === 'email'));
      if (email?.contact) {
        return `mailto:${email.contact}`;
      }
    }
    return '';
  }, [booth]);

  const openChatWindow = () => {
    window.trackingEvent('OpenChatWindow', 'MobileProfile', booth.id);
    setModalOpen(false);
    showChatWindow(`${RONGCLOUD_CHATROOM_PREFIX}_booth_${booth.id}`, 'group', booth.name);
  };

  const clickMeetup = () => {
    window.trackingEvent('Meetup', 'MobileProfile', booth.id);
  };

  const clickLeaveMessage = () => {
    window.trackingEvent('Leave a Message', 'MobileProfile', booth.id);
  };

  const onPlayButtonClick = () => window.open(VideoSources[parseInt(booth.boothNumber, 10) - 1], '_blank');

  const SelectedLogoContainer = (() => {
    switch (APP) {
      case 'ms624':
        return (
          <LogoContainer624>
            <StyledPlayButton onClick={onPlayButtonClick} src={playButton} alt="Play Button" />
            <StyledImg hasLogo={booth.logo} src={booth.logo || LogoPlaceholder} />
          </LogoContainer624>
        );
      default: return (
        <LogoContainer>
          <StyledPlayButton src={playButton} alt="Play Button" />
          <StyledImg hasLogo={booth.logo} src={booth.logo || LogoPlaceholder} />
        </LogoContainer>
      );
    }
  })();

  const shouldShowFab = (
    APP !== 'ms624'
    && (
      booth.meetupUrl
      || mailToLink
      || APP !== 'paloalto623'
    )
  );

  return (
    <>
      <BackContainer>
        <BoothBreadcrumbs boothName={booth.name} />
      </BackContainer>
      <FlexContainer>
        {SelectedLogoContainer}
        <NameText>{booth.name}</NameText>
      </FlexContainer>
      {shouldShowFab && (
        <PopupBtn onClick={() => setModalOpen(true)}>
          <StyledIcon src={InsertInvitationIcon} alt="chatroom" />
        </PopupBtn>
      )}
      <StyledModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <BubbleBtnGroup>
          {
            booth.meetupUrl && (
              <StyledHyperLink href={booth.meetupUrl} target="_blank" rel="noopener noreferrer">
                <BunnbleBtnContainer onClick={clickMeetup}>
                  <BubbleBtnText>
                    Meetup
                  </BubbleBtnText>
                  <BubbleBtn>
                    <StyledIcon src={InsertInvitationIcon} alt="Insert invitation" />
                  </BubbleBtn>
                </BunnbleBtnContainer>
              </StyledHyperLink>
            )
          }
          {
            mailToLink && (
              <StyledHyperLink href={mailToLink} target="_blank" rel="noopener noreferrer">
                <BunnbleBtnContainer onClick={clickLeaveMessage}>
                  <BubbleBtnText>
                    Leave a Message
                  </BubbleBtnText>
                  <BubbleBtn>
                    <StyledIcon src={EmailIcon} alt="Leave a message" />
                  </BubbleBtn>
                </BunnbleBtnContainer>
              </StyledHyperLink>
            )
          }
          {
            APP !== 'paloalto623' && (
              <BunnbleBtnContainer onClick={openChatWindow}>
                <BubbleBtnText>
                  {
                    APP === 'hkstp608' ? 'Public Chat' : 'Chat'
                  }
                </BubbleBtnText>
                <BubbleBtn>
                  <StyledIcon src={chatroomIcon} alt="Chatroom" />
                </BubbleBtn>
              </BunnbleBtnContainer>
            )
          }
          <PopupBtn onClick={() => setModalOpen(false)}>
            <StyledIcon src={CloseBtn} alt="Close" />
          </PopupBtn>
        </BubbleBtnGroup>
      </StyledModal>
    </>
  );
};

MobileProfile.propTypes = {
  booth: propTypes.shape().isRequired,
};

export default MobileProfile;
